import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/components/ui/AppIcon';
import { Section } from '@/features/projects/types';
import { BUTTON_STYLES, ButtonStyleId, getButtonStyles } from '@/constants/layoutTypes';

interface EditableCtaWrapperProps {
    text: string;
    link?: string;
    action?: 'scroll' | 'url';
    buttonStyle?: ButtonStyleId;
    primaryColor: string;
    isEditing: boolean;
    onUpdate: (text: string, link: string, action: 'scroll' | 'url') => void;
    onStyleChange?: (style: ButtonStyleId) => void;
    sections: Section[];
    children: React.ReactNode;
}

export function EditableCtaWrapper({
    text,
    link,
    action,
    buttonStyle = 'rounded',
    primaryColor,
    isEditing,
    onUpdate,
    onStyleChange,
    sections,
    children
}: EditableCtaWrapperProps) {
    const [showPopover, setShowPopover] = useState(false);
    const [tempText, setTempText] = useState(text);
    const [tempAction, setTempAction] = useState<'scroll' | 'url'>(action || 'scroll');
    const [tempLink, setTempLink] = useState(link || (sections.length > 0 ? sections[0].id : ''));
    const [tempStyle, setTempStyle] = useState<ButtonStyleId>(buttonStyle);
    const [mounted, setMounted] = useState(false);

    // Track mount for portal rendering (SSR safety)
    useEffect(() => {
        setMounted(true);
    }, []);

    // Default to first non-navbar section if scroll
    useEffect(() => {
        if (!link && tempAction === 'scroll' && sections.length > 0) {
            const firstTarget = sections.find(s => s.type !== 'navbar' && s.type !== 'footer')?.id;
            if (firstTarget) setTempLink(firstTarget);
        }
    }, [link, tempAction, sections]);

    const handleSave = () => {
        onUpdate(tempText, tempLink, tempAction);
        if (onStyleChange && tempStyle !== buttonStyle) {
            onStyleChange(tempStyle);
        }
        setShowPopover(false);
    };

    if (!isEditing) return <>{children}</>;

    // Render popup via portal so it escapes @container / transform containing blocks
    const popoverContent = showPopover && mounted ? createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={() => setShowPopover(false)}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/30" />

            {/* Popover Card */}
            <div
                className="relative w-80 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 text-left animate-in fade-in zoom-in-95"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                    <h4 className="font-semibold text-gray-900 text-sm">Edit Button</h4>
                    <button onClick={() => setShowPopover(false)} className="text-gray-400 hover:text-gray-600">
                        <Icon name="XMarkIcon" className="w-4 h-4" />
                    </button>
                </div>

                <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    {/* Button Style Selector */}
                    {onStyleChange && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">Button Style</label>
                            <div className="grid grid-cols-2 gap-2">
                                {BUTTON_STYLES.map((style) => {
                                    const styleProps = getButtonStyles(style.id, primaryColor);
                                    return (
                                        <button
                                            key={style.id}
                                            onClick={() => setTempStyle(style.id)}
                                            className={`p-2 rounded-lg border-2 transition-all ${tempStyle === style.id
                                                ? 'border-violet-500 bg-violet-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <div
                                                className={`${style.className} py-1.5 px-3 text-xs mx-auto mb-1`}
                                                style={styleProps}
                                            >
                                                {style.id === 'rounded' ? '●●●' :
                                                    style.id === 'square' ? '■■■' :
                                                        style.id === 'outline' ? '○○○' : '◆◆◆'}
                                            </div>
                                            <p className="text-xs font-medium text-gray-700">{style.label}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Text Input */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Button Text</label>
                        <input
                            type="text"
                            value={tempText}
                            onChange={(e) => setTempText(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-500 text-gray-900"
                        />
                    </div>

                    {/* Action Type */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setTempAction('scroll')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${tempAction === 'scroll' ? 'bg-white shadow text-violet-700' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Scroll to Section
                            </button>
                            <button
                                onClick={() => setTempAction('url')}
                                className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${tempAction === 'url' ? 'bg-white shadow text-violet-700' : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                Open Link
                            </button>
                        </div>
                    </div>

                    {/* Target */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {tempAction === 'scroll' ? 'Target Section' : 'External URL'}
                        </label>
                        {tempAction === 'scroll' ? (
                            <select
                                value={tempLink}
                                onChange={(e) => setTempLink(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-500 bg-white text-gray-900"
                            >
                                {sections.filter(s => s.type !== 'navbar' && s.type !== 'footer').map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.type.charAt(0).toUpperCase() + s.type.slice(1)} ({s.visible ? 'Visible' : 'Hidden'})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={tempLink}
                                onChange={(e) => setTempLink(e.target.value)}
                                placeholder="https://"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-violet-500 text-gray-900"
                            />
                        )}
                    </div>

                    <div className="pt-2">
                        <button
                            onClick={handleSave}
                            className="w-full py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <div className="relative group inline-block">
            {children}

            {/* Edit Trigger - Visible on hover/group-hover */}
            <button
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowPopover(true);
                    setTempText(text);
                    setTempAction(action || 'scroll');
                    setTempLink(link || '');
                    setTempStyle(buttonStyle);
                }}
                className="absolute -top-3 -right-3 p-1.5 bg-violet-600 text-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-violet-700 hover:scale-110"
                title="Edit Button"
            >
                <Icon name="PencilSquareIcon" className="w-4 h-4" />
            </button>

            {/* Portal-rendered popover */}
            {popoverContent}
        </div>
    );
}
